import classNames from "classnames";
import { Field, Form, Formik, FormikConfig, FormikProps } from "formik";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";

import style from "./general-setting-view.scss";
import { API_KEY_SCOPES, ApiKeyScope, getApiKeyScopeLocalizationKey, isApiKeyExpired } from "domain/apiKeys";
import { NAME_MAX_LENGTH, NOTES_MAX_LENGTH } from "domain/globalConstants";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { createDateLabel } from "utils/format";

import testIds from "testIds.json";

interface Props {
    name: string;
    description: string;
    scope: ApiKeyScope;
    expiration?: string;
    onSuccess: (values: FormValues) => Promise<void>;
}

export interface FormValues {
    name: string;
    description: string;
    scope: ApiKeyScope;
    expiration?: string;
}

export default function EditApiKeyForm(props: Props): JSX.Element {
    const { t } = useTranslation();

    const [descriptionCharactersLeft, setDescriptionCharactersLeft] = React.useState(
        NOTES_MAX_LENGTH - props.description.length
    );
    const descriptionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescriptionCharactersLeft(event.target.maxLength - event.target.value.length);
    };
    const submitHandler: FormikConfig<FormValues>["onSubmit"] = (values) => props.onSuccess(values);
    const scopeOptions = API_KEY_SCOPES.map((each) => (
        <option key={each} value={each}>
            {t(getApiKeyScopeLocalizationKey(each))}
        </option>
    ));

    return (
        <Formik
            initialValues={{
                name: props.name,
                description: props.description,
                scope: props.scope,
            }}
            onSubmit={submitHandler}
            validationSchema={object().shape({
                name: string().required(t("EditApiKey.nameRequired")),
            })}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({ values, handleChange, handleBlur, errors }: FormikProps<FormValues>) => {
                return (
                    <Form>
                        <div className={style.mainForm}>
                            <div className={form.formFields}>
                                <label htmlFor="name" className={form.label}>
                                    {t("Common.nameWithColon")}
                                </label>
                                <div className={form.formFieldsColumnDirection}>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        className={classNames(form.input, style.inputWidth)}
                                        maxLength={NAME_MAX_LENGTH}
                                        autoFocus
                                        onBlur={handleBlur}
                                        defaultValue={values.name}
                                        onChange={handleChange}
                                        data-testid={
                                            testIds.workArea.support.apiKeys.manageApiKeyDialog.nameInput.itself
                                        }
                                    />
                                    <div className={form.error}>{errors.name}</div>
                                </div>
                            </div>

                            <div className={form.formFields}>
                                <label htmlFor="scope" className={form.label}>
                                    {t("ApiKeys.scopeWithColon")}
                                </label>
                                <select
                                    id="scope"
                                    className={classNames(form.select, style.inputWidth)}
                                    name="scope"
                                    value={values.scope}
                                    data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.scopeSelect}
                                    disabled
                                >
                                    {scopeOptions}
                                </select>
                            </div>

                            {values.expiration && (
                                <div className={form.formFields}>
                                    <div className={form.formFieldsColumnDirection}>
                                        <label htmlFor="expiration" className={form.label}>
                                            {t("Common.expirationDateWithColon")}
                                        </label>
                                    </div>
                                    {/* TODO BCC-5878 Change color when expired. */}
                                    <div>{values.expiration}</div>
                                </div>
                            )}

                            <div className={form.formFields}>
                                <div className={form.formFieldsColumnDirection}>
                                    <span className={form.optional}>{t("Common.optional")}</span>
                                    <label htmlFor="description" className={classNames(form.label)}>
                                        {t("ApiKeys.descriptionWithColon")}
                                    </label>
                                </div>
                                <div className={style.descriptionContainer}>
                                    <Field
                                        as="textarea"
                                        id="description"
                                        className={classNames(form.input, style.inputWidth, style.descriptionHeight)}
                                        name="description"
                                        maxLength={NOTES_MAX_LENGTH}
                                        value={values.description}
                                        onBlur={handleBlur}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                            handleChange(e);
                                            descriptionChangeHandler(e);
                                        }}
                                        data-testid={
                                            testIds.workArea.support.apiKeys.manageApiKeyDialog.descriptionInput.itself
                                        }
                                    />
                                    <span className={form.optional}>
                                        {t("GenerateApiKeyForm.charactersLeft", {
                                            remainingCharacters: descriptionCharactersLeft.toString(),
                                            descriptionMaxLength: NOTES_MAX_LENGTH.toString(),
                                        })}
                                    </span>
                                </div>
                            </div>

                            {props.expiration && (
                                <div className={form.formFields}>
                                    <div className={form.formFieldsColumnDirection}>
                                        <label className={form.label}>{t("Common.expirationDateWithColon")}</label>
                                    </div>
                                    <div
                                        className={classNames({
                                            [style.expiredDate]: isApiKeyExpired(props.expiration),
                                        })}
                                        data-testid={
                                            testIds.workArea.support.apiKeys.manageApiKeyDialog.expirationDateLabel
                                        }
                                    >
                                        {createDateLabel(props.expiration)}
                                    </div>
                                </div>
                            )}

                            <div className={classNames(style.moveright, style.buttonContainer)}>
                                <button
                                    type="submit"
                                    className={classNames(buttons.primaryButtonWithoutIcon)}
                                    data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.saveButton}
                                >
                                    {t("Common.save")}
                                </button>
                            </div>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}
